import { PageTemplateFragment } from '@scentregroup/shared/graphql'
import { isDefinedAndNotNull } from '@scentregroup/shared/lib'
import * as Sentry from '@sentry/nextjs'
import {
  SGPageTemplateModuleWithAdZones,
  SGPageTemplateProductCollection,
  SGPageTemplateDynamicContent,
  SGPageTemplateContentEntry,
  SGPageTemplateProduct,
  SGPageTemplateAdZone,
} from '../../../../../src/types/page-templates'
import {
  IIssue,
  IssueType,
  IssueSeverity,
} from '../../../../../src/types/marketplace-product'
import { parseContentEntry } from './content-entry'

type SGPageTemplateFragmentModule = PageTemplateFragment['modules'][0]

type SGPageTemplateFragmentContentCollectionModule = Extract<
  SGPageTemplateFragmentModule,
  { __typename: 'SGPageTemplateContentCollection' }
>

type SGPageTemplateFragmentContentCollectionModuleItem =
  SGPageTemplateFragmentContentCollectionModule['items'][0]

type SGPageTemplateFragmentProductCollectionModule = Extract<
  SGPageTemplateFragmentModule,
  { __typename: 'SGPageTemplateProductCollection' }
>

function parseContentCollectionItems(
  items: SGPageTemplateFragmentContentCollectionModule['items']
): (SGPageTemplateContentEntry | SGPageTemplateAdZone)[] {
  if (!items) {
    return []
  }
  return items.flatMap(item => parseContentCollectionItem(item) || [])
}

function parseContentCollectionItem(
  item: SGPageTemplateFragmentContentCollectionModuleItem
): (SGPageTemplateContentEntry | SGPageTemplateAdZone) | null {
  if (!item) {
    return null
  }
  switch (item.__typename) {
    case 'SGPageTemplateContentEntry':
      return parseContentEntry(item)
    case 'SGPageTemplateAdZone':
      return item
    default: {
      const _exhaustive: never = item
      return null
    }
  }
}

function parseProductCollectionItems(
  items: SGPageTemplateFragmentProductCollectionModule['items']
): SGPageTemplateProduct[] {
  if (!items) {
    return []
  }

  return items.flatMap(
    (item): SGPageTemplateProduct | SGPageTemplateProduct[] => {
      if (!item) {
        return []
      }
      const { issues } = item
      let productIssues: IIssue[] = []

      if (issues) {
        productIssues = issues.flatMap(issue => {
          if (!issue) {
            return []
          }

          const { type: issueType, severity: issueSeverity } = issue

          const type = parseIssueType(issueType)
          const severity = parseIssueSeverity(issueSeverity)

          if (!type || !severity) {
            return []
          }

          return {
            severity,
            type,
          }
        })
      }
      return {
        __typename: 'SGPageTemplateProduct',
        shop: item.shop,
        brand: item.brand,
        variantGroupCode: item.variantGroupCode ?? item.sku,
        kind: item.kind,
        sku: item.sku,
        title: item.title,
        image: item.image,
        price: item.price ?? null,
        discountedPrice: item.discountedPrice,
        percentDiscount: item.percentDiscount,
        callToAction: item.callToAction,
        issues: productIssues ?? null,
        category: item.category
          ? item.category.filter(isDefinedAndNotNull)
          : null,
        promotions: item.promotions
          ? item.promotions.filter(isDefinedAndNotNull)
          : [],
      }
    }
  )
}

export function parseModule(
  module: SGPageTemplateFragmentModule
): SGPageTemplateModuleWithAdZones | SGPageTemplateModuleWithAdZones[] {
  if (!module) {
    return []
  }

  switch (module.__typename) {
    case 'SGPageTemplateBasicContent': {
      return {
        __typename: 'SGPageTemplateBasicContent',
        title: module.title || '',
        shortDescription: module?.shortDescription || '',
        bodyCopy: module.bodyCopy || '',
        callsToAction: module.callsToAction,
      }
    }
    case 'SGPageTemplateContentCollection': {
      const items = parseContentCollectionItems(module.items)

      if (!module.analyticsMetadata?.displayId) {
        Sentry.captureMessage(
          `No displayId found for ${module.title}, this shouldn't ever be undefined something must be wrong`,
          'warning'
        )
      }

      return {
        __typename: 'SGPageTemplateContentCollection',
        kind: module.contentCollectionKind,
        title: module.title,
        shortDescription: module.shortDescription,
        ...(module.analyticsMetadata?.displayId
          ? {
              analyticsMetadata: {
                correlationIds: module.analyticsMetadata?.correlationIds
                  ? [...module.analyticsMetadata.correlationIds]
                  : [],
                displayId: module.analyticsMetadata?.displayId,
                position: module.analyticsMetadata?.position ?? null,
              },
            }
          : {}),
        items,
        callToAction: module.callToAction,
      }
    }
    case 'SGPageTemplateProductCollection': {
      const items = parseProductCollectionItems(module.items)
      return {
        __typename: 'SGPageTemplateProductCollection',
        kind: module.productCollectionKind,
        title: module.title,
        shortDescription: module.shortDescription,
        url: module.url,
        ...(module.analyticsMetadata?.displayId
          ? {
              analyticsMetadata: {
                correlationIds: module.analyticsMetadata?.correlationIds
                  ? [...module.analyticsMetadata.correlationIds]
                  : [],
                displayId: module.analyticsMetadata?.displayId,
                position: module.analyticsMetadata?.position ?? null,
              },
            }
          : {}),
        image: module.image,
        items,
      } as SGPageTemplateProductCollection
    }
    case 'SGPageTemplateDynamicContent': {
      return {
        __typename: 'SGPageTemplateDynamicContent',
        kind: module.dynamicContentKind,
      } as SGPageTemplateDynamicContent
    }
    default:
      return []
  }
}

function parseIssueType(input: string | null | undefined): IssueType | null {
  const issueTypeValues: string[] = Object.values(IssueType)

  if (!input) {
    return null
  }

  if (issueTypeValues.includes(input)) {
    return input as IssueType
  }

  return null
}

function parseIssueSeverity(
  input: string | null | undefined
): IssueSeverity | null {
  const issueSeverityValues: string[] = Object.values(IssueSeverity)

  if (!input) {
    return null
  }

  if (issueSeverityValues.includes(input)) {
    return input as IssueSeverity
  }
  return null
}
